import { api } from '@/lib/api';
import type { Artifact, ArtifactVersion } from '@innermate/shared';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo, useState } from 'react';

interface UseSessionArtifactsResult {
  artifacts: Artifact[];
  selected: Artifact | null;
  /** selectedId 가 설정됐지만 아직 목록에 없는 경우 (새 artifact 생성 직후 리스트 refetch 대기). */
  isPendingLoad: boolean;
  effectiveVersion: number | null;
  versionContent: ArtifactVersion | null;
  isContentLoading: boolean;
  selectArtifact: (artifactId: string | null) => void;
  /** version=null 이면 selected.currentVersion 으로 자동. */
  selectVersion: (version: number | null) => void;
  /** SSE artifact_created/updated 후 호출 — 목록 + 본문 캐시 무효화 + 자동 선택. */
  handleArtifactChange: (artifactId: string) => void;
}

/**
 * 세션의 artifact head 목록을 react-query 로 캐싱하고, 우측 패널에 표시할
 * artifact + version 선택 상태를 관리한다. 본문도 별도 query 로 fetch.
 *
 * Session.tsx 의 SSE 콜백 (`onArtifactCreated` / `onArtifactUpdated`) 이
 * `handleArtifactChange(artifactId)` 만 호출하면 목록과 본문이 자동 refetch 되고
 * 새 artifact 가 자동 선택된다.
 */
export function useSessionArtifacts(sessionId: string): UseSessionArtifactsResult {
  const qc = useQueryClient();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [pinnedVersion, setPinnedVersion] = useState<number | null>(null);

  const listQuery = useQuery({
    queryKey: ['artifacts', 'session', sessionId],
    queryFn: () => api.artifactSessionList(sessionId),
    staleTime: 10_000,
  });

  const artifacts = useMemo(() => listQuery.data?.artifacts ?? [], [listQuery.data]);
  const selected = useMemo(
    () => (selectedId ? (artifacts.find((a) => a.id === selectedId) ?? null) : null),
    [artifacts, selectedId],
  );

  const effectiveVersion = selected ? (pinnedVersion ?? selected.currentVersion) : null;

  const contentQuery = useQuery({
    queryKey: ['artifact', selected?.id, 'version', effectiveVersion],
    queryFn: () =>
      selected && effectiveVersion !== null
        ? api.artifactGetVersion(selected.id, effectiveVersion)
        : Promise.resolve(null),
    enabled: !!selected && effectiveVersion !== null,
    staleTime: 60_000,
  });

  const selectArtifact = useCallback((artifactId: string | null) => {
    setSelectedId(artifactId);
    setPinnedVersion(null);
  }, []);

  const selectVersion = useCallback((version: number | null) => {
    setPinnedVersion(version);
  }, []);

  const handleArtifactChange = useCallback(
    (artifactId: string) => {
      // 목록 + 해당 artifact 의 모든 버전 캐시 무효화 → react-query 가 refetch.
      void qc.invalidateQueries({ queryKey: ['artifacts', 'session', sessionId] });
      void qc.invalidateQueries({ queryKey: ['artifact', artifactId] });
      // 새 artifact (또는 갱신된 것) 를 자동 선택, 최신 버전 표시.
      setSelectedId(artifactId);
      setPinnedVersion(null);
    },
    [qc, sessionId],
  );

  return {
    artifacts,
    selected,
    isPendingLoad: selectedId !== null && selected === null,
    effectiveVersion,
    versionContent: contentQuery.data ?? null,
    isContentLoading: contentQuery.isLoading || contentQuery.isFetching,
    selectArtifact,
    selectVersion,
    handleArtifactChange,
  };
}
